import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Briefcase, Share2, Hotel, HeartPulse, Sparkles, ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { categoryName } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import AdminEditableImage from "@/components/AdminEditableImage";
import { categoryGraphics } from "@/lib/categoryGraphics";
import { pageTitle, SITE_ORIGIN } from "@/lib/seo";

const META = {
  career: { icon: Briefcase, img: "/category-backgrounds/category-career.webp", editKey: "category_career", smart: "/servizi-smart/cv-smart-premium", smartKey: "smart.cv.title", smartLive: true },
  "business-social": { icon: Share2, img: "/category-backgrounds/category-business-social.webp", editKey: "category_business_social", smart: "/servizi-smart", smartKey: "smart.social.title" },
  hospitality: { icon: Hotel, img: "/category-backgrounds/category-hospitality.webp", editKey: "category_hospitality", smart: "/servizi-smart", smartKey: "smart.hospitality.title" },
  oss: { icon: HeartPulse, img: "/category-backgrounds/category-oss.webp", editKey: "category_oss" },
  digitale: { icon: Sparkles, img: "https://images.pexels.com/photos/30547616/pexels-photo-30547616.jpeg", editKey: "category_digitale" },
};

const CategoryPage = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const g = categoryGraphics(slug);
  const meta = META[slug] || { icon: g.Icon, img: g.img, editKey: g.editKey };
  const Icon = meta.icon;

  useEffect(() => {
    setLoading(true);
    api.get("/categories").then(({ data }) => setCategory(data.find((c) => c.slug === slug) || { slug, name_it: slug, name_en: slug }));
    api.get("/products", { params: { category: slug } }).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [slug]);

  const name = category ? categoryName(category, lang) : slug;
  const catDesc = category ? (lang === "en" ? (category.desc_en || category.desc_it) : (category.desc_it || category.desc_en)) : "";
  const subtitle = catDesc || t("products.subtitle");

  return (
    <div data-testid={`category-page-${slug}`}>
      <Seo
        title={pageTitle(name)}
        description={lang === "en"
          ? `Digital products in the ${name} category on LearnSphereHub: downloadable files ready to use after purchase.`
          : `Prodotti digitali della categoria ${name} su LearnSphereHub: file scaricabili pronti all'uso dopo l'acquisto.`}
        path={`/categoria/${slug}`}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: t("products.title"), item: `${SITE_ORIGIN}/prodotti` },
            { "@type": "ListItem", position: 3, name, item: `${SITE_ORIGIN}/categoria/${slug}` },
          ],
        }}
      />
      <section className="relative bg-brand-navy text-white overflow-hidden">
        {(() => {
          const bg = category?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${category.image_url}` : meta.img;
          return bg ? <div className="absolute inset-0" aria-hidden="true"
            style={{ backgroundColor: "#050d1f", backgroundImage: `url(${bg})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }} /> : null;
        })()}
        <div className="absolute inset-0" aria-hidden="true" style={{ background: "linear-gradient(90deg, rgba(5,13,31,0.55) 0%, rgba(5,13,31,0.22) 48%, rgba(5,13,31,0.04) 75%, rgba(5,13,31,0) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24">
          <div className="h-14 w-14 rounded-2xl bg-brand-electric/20 flex items-center justify-center text-white"><Icon className="h-7 w-7" /></div>
          <h1 className="mt-6 text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{name}</h1>
          <p className="mt-4 text-white/70 max-w-2xl">{subtitle}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {slug === "percorsi-guidati" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/percorsi/restart-dating-45" data-testid="card-restart-dating" className="group rounded-2xl overflow-hidden border border-brand-navy/10 bg-white hover:shadow-xl transition-shadow">
              <div className="relative aspect-[16/10] overflow-hidden bg-brand-navy">
                <img src="https://static.prod-images.emergentagent.com/jobs/72130633-4d95-425a-bae8-f0a72af37042/images/66c5779ed718d9757f43b3b507f755da0d954ecb29821eda0596468f9e2683ed.jpeg" alt="Restart Dating 45+" className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300" />
                <span className="absolute top-3 right-3 bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">2 versioni disponibili</span>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-brand-navy text-lg">Restart Dating 45+</h3>
                <p className="mt-2 text-sm text-brand-navy/60 line-clamp-2">Percorso pratico per tornare a conoscere persone con più chiarezza, metodo e serenità.</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-electric">Scopri il percorso →</span>
              </div>
            </Link>
          </div>
        ) : loading ? (
          <div className="py-20 text-center text-brand-navy/50">{t("common.loading")}</div>
        ) : products.length === 0 ? (
          <div className="py-20 text-center text-brand-navy/50">{t("products.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}

        {meta.smartKey && (
          <div className="mt-14 rounded-2xl bg-brand-navy text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              {meta.smartLive ? (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-electric"><Sparkles className="h-4 w-4" /> {t("common.availableNow")}</span>
              ) : (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-orange"><Sparkles className="h-4 w-4" /> {t("common.comingSoon")}</span>
              )}
              <h3 className="mt-3 text-2xl font-bold font-heading">{t(meta.smartKey)}</h3>
            </div>
            <Button asChild className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
              <Link to={meta.smart} data-testid="cat-smart-link">{meta.smartLive ? t("common.tryNow") : t("common.discoverSmart")} <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
